import { Component, OnInit, Input, EventEmitter, Output, ElementRef, ViewChild, TemplateRef, signal, OnChanges, NO_ERRORS_SCHEMA } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { ConfigSchemaFlowDefinition, ConfigSchemaSectionAccessLevel, ConfigSchemaSectionDocumentClass } from '../../../../models/config-schema.model';
import { FlowDefinition } from '../../../../models/flow.model';
import { AuthService } from '../../../../services/auth.service';
import { Enums } from '../../../../shared/enums';
import { Utils } from '../../../../shared/utils';
import { IMPORTS, PROVIDERS } from './_imports';
import { AccessLevelType } from 'src/app/models/edocs.model';

@Component({
    selector: 'fdd-hot-configs',
    templateUrl: './fdd-hot-configs.component.html',
    styleUrl: './fdd-hot-configs.component.scss',
    standalone: true,
    imports: IMPORTS,
    providers: PROVIDERS,
    schemas: [NO_ERRORS_SCHEMA]
})
export class FlowDefinitionDetailsHotConfigsComponent implements OnInit, OnChanges {
    // #region [ViewChild]
    @ViewChild('configSchemaRef') configSchemaRef: TemplateRef<HTMLElement>;
    @ViewChild('configSchemaEditorRef') configSchemaEditorRef: ElementRef;
    // #endregion

    // #region [Type props]
    Utils = Utils;
    // #endregion

    // #region [props]
    isLatestVersion: boolean;
    configSchemaDialog: MatDialogRef<HTMLElement, any>;
    // #endregion

    // #region [signals]
    $model = signal(null as FlowDefinition);
    $configSchema = signal(new ConfigSchemaFlowDefinition());
    $configSchemaFlat = signal(null as string);
    $cancellationDocumentClass = signal(null as ConfigSchemaSectionDocumentClass);
    $cancellationAccessLevel = signal(null as ConfigSchemaSectionAccessLevel);
    $isAutoDeactivateSinceFlowDefinitionActivation = signal(false);
    $isAutoDeactivateOnDateTime = signal(false);
    $isAutoActivateSinceFlowDefinitionPublication = signal(false);
    $isAutoActivateOnDateTime = signal(false);
    $isAutoCancelSinceFlowInstanceStart = signal(false);
    $isAutoCancelSinceFlowInstanceLastUpdate = signal(false);
    $isAutoCancelOnDateTime = signal(false);
    $isCancellingByDeliberation = signal(false);
    $isReadOnlyMode = signal(false);
    // #endregion

    // #region [Input/Output]
    @Input() _model: FlowDefinition;
    @Output() _updateFlowDefinitionEvent = new EventEmitter<FlowDefinition>();
    @Output() _closeEvent = new EventEmitter<any>();
    // #endregion

    constructor(
        private route: ActivatedRoute,
        private dialog: MatDialog,
        private toastr: ToastrService,
        public authService: AuthService
    ) {
        this.isLatestVersion = Utils.isNullOrEmpty(this.route.snapshot.paramMap.get('version'));
    }

    ngOnInit() {
        let interval = setInterval(async () => {
            if (this.$model() == null) return;
            clearInterval(interval);

            if (this.$model().configSchema != null) {
                Utils.setSignal(this.$configSchema, Object.assign(this.$configSchema(), JSON.parse(this.$model().configSchema)));
            }

            this.initConfigSchemaInfo();
        }, 200);
    }

    ngOnChanges() {
        this.$model.set(this._model);
    }

    clearAutoCancelChildren(event) {
        Utils.updSignal(this.$configSchema, x => {
            x.hasAutomaticFlowInstanceCancellation = event;
            return x;
        });

        this.$isAutoCancelSinceFlowInstanceStart.set(false);
        this.$isAutoCancelSinceFlowInstanceLastUpdate.set(false);
        this.$isAutoCancelOnDateTime.set(false);
        Utils.updSignal(this.$configSchema, x => {
            x.daysSinceFlowInstanceStartToAutoCancel = null;
            x.daysSinceFlowInstanceLastUpdateToAutoCancel = null;
            x.dateTimeToAutoCancelFlowInstance = null;
            return x;
        });
    }

    clearAutoCancelSinceFlowInstanceStart(event) {
        this.$isAutoCancelSinceFlowInstanceStart.set(event);

        if (!event) {
            Utils.updSignal(this.$configSchema, x => {
                x.daysSinceFlowInstanceStartToAutoCancel = null;
                return x;
            });
        }
    }

    clearAutoCancelSinceFlowInstanceLastUpdate(event) {
        this.$isAutoCancelSinceFlowInstanceLastUpdate.set(event);

        if (!event) {
            Utils.updSignal(this.$configSchema, x => {
                x.daysSinceFlowInstanceLastUpdateToAutoCancel = null;
                return x;
            });
        }
    }

    clearAutoCancelOnDateTime(event) {
        this.$isAutoCancelOnDateTime.set(event);

        if (!event) {
            Utils.updSignal(this.$configSchema, x => {
                x.dateTimeToAutoCancelFlowInstance = null;
                return x;
            });
        }
    }

    clearAutoActivateChildren(event) {
        Utils.updSignal(this.$configSchema, x => {
            x.hasAutomaticFlowDefinitionActivation = event;
            return x;
        });

        this.$isAutoActivateSinceFlowDefinitionPublication.set(false);
        this.$isAutoActivateOnDateTime.set(false);
        Utils.updSignal(this.$configSchema, x => {
            x.daysSinceFlowDefinitionPublicationToAutoActivate = null;
            x.dateTimeToAutoActivateFlowDefinition = null;
            return x;
        });
    }

    clearAutoActivateSinceFlowDefinitionPublication(event) {
        this.$isAutoActivateSinceFlowDefinitionPublication.set(event);

        if (!event) {
            Utils.updSignal(this.$configSchema, x => {
                x.daysSinceFlowDefinitionPublicationToAutoActivate = null;
                return x;
            });
        }
    }

    clearAutoActivateOnDateTime(event) {
        this.$isAutoActivateOnDateTime.set(event);

        if (!event) {
            Utils.updSignal(this.$configSchema, x => {
                x.dateTimeToAutoActivateFlowDefinition = null;
                return x;
            });
        }
    }

    clearAutoDeactivateChildren(event) {
        Utils.updSignal(this.$configSchema, x => {
            x.hasAutomaticFlowDefinitionDeactivation = event;
            return x;
        });

        this.$isAutoDeactivateSinceFlowDefinitionActivation.set(false);
        this.$isAutoDeactivateOnDateTime.set(false);
        Utils.updSignal(this.$configSchema, x => {
            x.daysSinceFlowDefinitionActivationToAutoDeactivate = null;
            x.dateTimeToAutoDeactivateFlowDefinition = null;
            return x;
        });
    }

    clearAutoDeactivateSinceFlowDefinitionActivation(event) {
        this.$isAutoDeactivateSinceFlowDefinitionActivation.set(event);

        if (!event) {
            Utils.updSignal(this.$configSchema, x => {
                x.daysSinceFlowDefinitionActivationToAutoDeactivate = null;
                return x;
            });
        }
    }

    clearAutoDeactivateOnDateTime(event) {
        this.$isAutoDeactivateOnDateTime.set(event);

        if (!event) {
            Utils.updSignal(this.$configSchema, x => {
                x.dateTimeToAutoDeactivateFlowDefinition = null;
                return x;
            });
        }
    }

    clearCancellationByDeliberation(event) {
        this.$isCancellingByDeliberation.set(event);
        
        Utils.updSignal(this.$configSchema, x => {
            x.hasCancellationByDeliberation = event;
            if (!event) {
                x.cancellationDocumentClass = new ConfigSchemaSectionDocumentClass();
                x.cancellationAccessLevel = new ConfigSchemaSectionAccessLevel();
                Utils.setSignal(this.$cancellationDocumentClass, new ConfigSchemaSectionDocumentClass());
                Utils.setSignal(this.$cancellationAccessLevel, new ConfigSchemaSectionAccessLevel());
            }
            return x;
        });
    }

    showConfigSchema() {
        let tempObject = {
            model: Utils.decycleFlowDefinition(this.$model()),
            configSchema: JSON.parse(JSON.stringify(this.$configSchema()))
        };
        delete tempObject.model.configSchema;
        delete tempObject.model.flowObjectDefinitions;

        this.$configSchemaFlat.set(btoa(encodeURIComponent(JSON.stringify(tempObject))));

        this.configSchemaDialog = this.dialog.open(this.configSchemaRef, {
            minWidth: '800px'
        });

        this.configSchemaDialog.afterOpened().subscribe(() => {
            setTimeout(() => {
                this.configSchemaEditorRef.nativeElement.scrollLeft = 0;
                this.configSchemaEditorRef.nativeElement.scrollTop = 0;
            }, 1);
            setTimeout(() => {
                this.configSchemaEditorRef.nativeElement.style.opacity = '1';
            }, 100);
        });

        this.configSchemaDialog.afterClosed().subscribe(async () => {
            try {
                let modalObject = JSON.parse(decodeURIComponent(atob(this.configSchemaEditorRef.nativeElement.value)));
                delete modalObject.model.configSchema;

                // checa se o tipo da origem do conteúdo é o mesmo tipo do conteúdo atual (i.e. se a origem não é um FlowObjectDefinition)
                if (modalObject.model.targetId == null) {
                    this.toastr.error(Enums.Messages.InvalidConfigSchemaSourceType, Enums.Messages.Error, Utils.getToastrErrorOptions());
                    return;
                }

                // checa se o código-fonte foi alterado
                if (JSON.stringify(tempObject) != JSON.stringify(modalObject)) {
                    this.$configSchema.set(JSON.parse(JSON.stringify(modalObject.configSchema)) as ConfigSchemaFlowDefinition);
                }
            } catch (error) {
                this.toastr.error(Enums.Messages.InvalidFlowDefinitionConfigSchemaSource, Enums.Messages.Error, Utils.getToastrErrorOptions());
                return;
            }

            this.initConfigSchemaInfo();
        });
    }

    onSubmit() {
        if (!this.isLatestVersion) return false;

        Utils.updSignal(this.$configSchema, x => {
            x.cancellationDocumentClass = this.$cancellationDocumentClass();
            x.cancellationAccessLevel = this.$cancellationAccessLevel();
            return x;
        });

        if (this.$configSchema().hasCancellationByDeliberation) {
            if (Utils.isNullOrEmpty(this.$cancellationDocumentClass()?.id) || Utils.isNullOrEmpty(this.$cancellationAccessLevel()?.id)) {
                this.toastr.error(Enums.Messages.InvalidCancelByDeliberationOptions, Enums.Messages.DataValidationError, Utils.getToastrErrorOptions());
                return false;
            }

            if (this.$cancellationAccessLevel()?.id == AccessLevelType.Confidential && Utils.isNullOrEmpty(this.$cancellationAccessLevel()?.legalReasoningsIds)) {
                this.toastr.error(Enums.Messages.InvalidCancelByDeliberationOptions, Enums.Messages.DataValidationError, Utils.getToastrErrorOptions());
                return false;
            }
        }

        if (
            (
                this.$configSchema().hasAutomaticFlowDefinitionActivation
                && !this.$isAutoActivateSinceFlowDefinitionPublication()
                && !this.$isAutoActivateOnDateTime()
            ) || (
                this.$isAutoActivateSinceFlowDefinitionPublication()
                && this.$configSchema().daysSinceFlowDefinitionPublicationToAutoActivate <= 0
            ) || (
                this.$isAutoActivateOnDateTime()
                && this.$configSchema().dateTimeToAutoActivateFlowDefinition == null
            )
        ) {
            this.toastr.error(Enums.Messages.InvalidAutoActivateOptions, Enums.Messages.DataValidationError, Utils.getToastrErrorOptions());
            return false;
        }

        if (
            (
                this.$configSchema().hasAutomaticFlowDefinitionDeactivation
                && !this.$isAutoDeactivateSinceFlowDefinitionActivation()
                && !this.$isAutoDeactivateOnDateTime()
            ) || (
                this.$isAutoDeactivateSinceFlowDefinitionActivation()
                && this.$configSchema().daysSinceFlowDefinitionActivationToAutoDeactivate <= 0
            ) || (
                this.$isAutoDeactivateOnDateTime()
                && this.$configSchema().dateTimeToAutoDeactivateFlowDefinition == null
            )
        ) {
            this.toastr.error(Enums.Messages.InvalidAutoDeactivateOptions, Enums.Messages.DataValidationError, Utils.getToastrErrorOptions());
            return false;
        }

        if (
            (
                this.$configSchema().hasAutomaticFlowInstanceCancellation
                && !this.$isAutoCancelSinceFlowInstanceStart()
                && !this.$isAutoCancelSinceFlowInstanceLastUpdate()
                && !this.$isAutoCancelOnDateTime()
            ) || (
                this.$isAutoCancelSinceFlowInstanceStart()
                && this.$configSchema().daysSinceFlowInstanceStartToAutoCancel <= 0
            ) || (
                this.$isAutoCancelSinceFlowInstanceLastUpdate()
                && this.$configSchema().daysSinceFlowInstanceLastUpdateToAutoCancel <= 0
            ) || (
                this.$isAutoCancelOnDateTime()
                && this.$configSchema().dateTimeToAutoCancelFlowInstance == null
            )
        ) {
            this.toastr.error(Enums.Messages.InvalidAutoCancelOptions, Enums.Messages.DataValidationError, Utils.getToastrErrorOptions());
            return false;
        }

        Utils.updSignal(this.$model, x => {
            x.configSchema = JSON.stringify(this.$configSchema());
            return x;
        });

        this._updateFlowDefinitionEvent.emit(this.$model());
    }

    unlistedModeChange(event) {
        Utils.updSignal(this.$model, x => {
            x.isUnlistedModeActive = event;
            return x;
        });
    }

    daysSinceFlowDefinitionActivationToAutoDeactivateChange(event) {
        Utils.updSignal(this.$configSchema, x => {
            x.daysSinceFlowDefinitionActivationToAutoDeactivate = event;
            return x;
        });
    }

    daysSinceFlowInstanceStartToAutoCancelChange(event) {
        Utils.updSignal(this.$configSchema, x => {
            x.daysSinceFlowInstanceStartToAutoCancel = event;
            return x;
        });
    }

    daysSinceFlowInstanceLastUpdateToAutoCancelChange(event) {
        Utils.updSignal(this.$configSchema, x => {
            x.daysSinceFlowInstanceLastUpdateToAutoCancel = event;
            return x;
        });
    }

    dateTimeToAutoActivateFlowDefinitionChange(event) {
        Utils.updSignal(this.$configSchema, x => {
            x.dateTimeToAutoActivateFlowDefinition = event;
            return x;
        });
    }

    dateTimeToAutoDeactivateFlowDefinitionChange(event) {
        Utils.updSignal(this.$configSchema, x => {
            x.dateTimeToAutoDeactivateFlowDefinition = event;
            return x;
        });
    }

    dateTimeToAutoCancelFlowInstanceChange(event) {
        Utils.updSignal(this.$configSchema, x => {
            x.dateTimeToAutoCancelFlowInstance = event;
            return x;
        });
    }

    onUpdateCancellationDocumentClass(event: ConfigSchemaSectionDocumentClass) {
        Utils.setSignal(this.$cancellationDocumentClass, event);
    }

    onUpdateCancellationAccessLevel(event: ConfigSchemaSectionAccessLevel) {
        Utils.setSignal(this.$cancellationAccessLevel, event);
    }

    closeForm() {
        this._closeEvent.emit();
    }

    // #region [===== private methods =====]
    // #endregion

    private initConfigSchemaInfo() {
        this.$isAutoActivateSinceFlowDefinitionPublication.set(this.$configSchema().daysSinceFlowDefinitionPublicationToAutoActivate != null);
        this.$isAutoActivateOnDateTime.set(this.$configSchema().dateTimeToAutoActivateFlowDefinition != null);
        this.$isAutoDeactivateSinceFlowDefinitionActivation.set(this.$configSchema().daysSinceFlowDefinitionActivationToAutoDeactivate != null);
        this.$isAutoDeactivateOnDateTime.set(this.$configSchema().dateTimeToAutoDeactivateFlowDefinition != null);
        this.$isAutoCancelSinceFlowInstanceStart.set(this.$configSchema().daysSinceFlowInstanceStartToAutoCancel != null);
        this.$isAutoCancelSinceFlowInstanceLastUpdate.set(this.$configSchema().daysSinceFlowInstanceLastUpdateToAutoCancel != null);
        this.$isAutoCancelOnDateTime.set(this.$configSchema().dateTimeToAutoCancelFlowInstance != null);
        this.$isCancellingByDeliberation.set(this.$configSchema().hasCancellationByDeliberation == true);

        Utils.setSignal(this.$cancellationDocumentClass, this.$configSchema().cancellationDocumentClass || new ConfigSchemaSectionDocumentClass());
        Utils.setSignal(this.$cancellationAccessLevel, this.$configSchema().cancellationAccessLevel || new ConfigSchemaSectionAccessLevel());
    }
}
